import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { tituloClinico } from "../../utils/textoClinico";
import { citaCoincideCobertura } from "../../utils/resumenCitas";
import { coberturaDeCita, textoCobertura } from "../../utils/coberturaPaciente";
import {
    calcularIntervalos,
    esDiaLaboral,
    estaDentroDeHorario,
    buscarCita,
    estadoDeCita,
    LEYENDA_ESTADOS,
    iniciales,
    nombreAbreviado,
    ocupacionDeDoctor,
    rangoHorario,
    posicionHoraActual,
    colorDoctor,
    contrasteSobre,
} from "../../utils/agendaCitas";
import "../../styles/CitasAgenda.css";

/* ============================================================================
   AGENDA · la cuadrícula de cupos

   Sustituye a la tabla de filas de ~90px donde sólo cabían cuatro franjas por
   pantalla y un cupo libre se veía igual que uno fuera de horario: una celda
   gris vacía.

   Sobre el "alternar azul y negro" de la cabecera antigua: NO era un fallo de
   estilos. Era `doctores.calcolor`, un color que se configura por médico en su
   ficha -- y 38 de los 51 lo tienen en #000000, que es lo que devuelve el
   selector cuando nadie lo ha tocado. Aquí se conserva esa identidad, pero en
   el avatar y en una barra de 3px en vez de teñir la cabecera entera.

   NO CAMBIA NINGÚN COMPORTAMIENTO. Clic en cupo libre y en cupo ocupado llaman
   al mismo manejador de siempre, que abre el mismo modal con los mismos datos.
   El menú contextual sigue saliendo con clic derecho en cualquier celda.

   NO HAY ESTADO "BLOQUEADO": no existe tabla de bloqueos de agenda en el
   esquema (verificado en la FASE 0). Se pintan sólo los estados que el backend
   distingue de verdad.
   ========================================================================== */

function IconoMas() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14" /><path d="M5 12h14" />
        </svg>
    );
}

function Esqueleto({ columnas = 4, filas = 8 }) {
    return (
        <div aria-busy="true" aria-label="Cargando agenda">
            {Array.from({ length: filas }).map((_, f) => (
                <div className="cagenda__skel-fila" key={f}>
                    <span className="cagenda__skel-celda cagenda__skel-celda--hora" />
                    {Array.from({ length: columnas }).map((__, c) => (
                        <span className="cagenda__skel-celda" key={c} />
                    ))}
                </div>
            ))}
        </div>
    );
}

function Vacio({ titulo, texto, accion }) {
    return (
        <div className="cagenda__vacio">
            <p className="cagenda__vacio-titulo">{titulo}</p>
            <p className="cagenda__vacio-texto">{texto}</p>
            {accion}
        </div>
    );
}

export default function AgendaCuadricula({
    doctores = [],
    citas = [],
    fecha,
    cargando = false,
    error = null,
    haySeleccion = true,
    // Ids de estado visibles; `null` = todos. Un estado filtrado NO se oculta:
    // se atenúa. Esconder un cupo ocupado lo haría parecer libre y alguien
    // intentaría agendar encima.
    estadosVisibles = null,
    filtroCobertura = null,
    // CITAS-VISTA-MEDICO: en la agenda del propio médico no se AGENDA -- un
    // cupo libre que ofreciera "Agendar" prometería algo que esa pantalla no
    // hace. Las citas SÍ siguen siendo accionables: ver `onAbrirCita`.
    soloLectura = false,
    onCupo,              // (doctor, hora, intervalo) -> el manejador de siempre
    onCitaPulsada,       // (cita, evento) -> sólo en soloLectura
    onMenuContextual,    // (evento, doctor, hora)
    onReintentar,
    onIrDiaSiguiente,
}) {
    const scrollRef = useRef(null);
    const gridRef = useRef(null);

    // El intervalo sale del dato de la especialidad (15/20/25/30/40 según el
    // caso), nunca de un número en duro.
    const intervalo =
        doctores[0]?.especialidades?.clinica_especialidades?.[0]?.intervalo_tiempo ?? null;

    const franjas = useMemo(() => {
        const clinica = doctores[0]?.clinica;
        if (!clinica || !intervalo) return [];
        return calcularIntervalos(clinica.horario_inicio, clinica.horario_fin, intervalo);
    }, [doctores, intervalo]);

    const posicionAhora = useMemo(
        () => posicionHoraActual(fecha, franjas, intervalo),
        [fecha, franjas, intervalo]
    );

    /* La línea de "ahora" se coloca en PÍXELES medidos sobre las filas, no con
       un porcentaje.

       El porcentaje que devuelve `posicionHoraActual` es correcto, pero es una
       fracción del RANGO HORARIO, y aplicado como `top: %` se resolvía contra
       la caja con scroll (553 px de alto visible) en vez de contra la
       cuadrícula entera (1388 px), y además ignoraba que la cabecera fija
       ocupa 54 px que no son horas. Por eso a las 13:31 la línea salía junto a
       las 10:10.

       Midiendo las celdas de hora reales sale exacto y sigue saliendo exacto
       aunque una fila crezca o cambie el alto de la cabecera. */
    const [topAhora, setTopAhora] = useState(null);

    useLayoutEffect(() => {
        const grid = gridRef.current;
        if (grid == null || posicionAhora == null) {
            setTopAhora(null);
            return;
        }
        const horas = grid.querySelectorAll(".cagenda__hora");
        if (horas.length === 0) return;
        const primera = horas[0];
        const ultima = horas[horas.length - 1];
        // `offsetTop` es relativo a la cuadrícula, que es el bloque contenedor.
        const inicioFilas = primera.offsetTop;
        const altoFilas = ultima.offsetTop + ultima.offsetHeight - inicioFilas;
        setTopAhora(inicioFilas + (altoFilas * posicionAhora) / 100);
    }, [posicionAhora, franjas.length, doctores.length]);

    // Al abrir, la agenda se sitúa en la hora actual: lo que le interesa a
    // quien la abre a media mañana no es la primera franja del día.
    useEffect(() => {
        if (topAhora == null || !scrollRef.current) return;
        const caja = scrollRef.current;
        caja.scrollTop = Math.max(0, topAhora - caja.clientHeight / 2);
    }, [topAhora]);

    if (error) {
        return (
            <div className="cagenda">
                <Vacio
                    titulo="No se pudo cargar la agenda"
                    texto="La fecha y la especialidad siguen seleccionadas: puedes reintentar sin perderlas."
                    accion={
                        onReintentar && (
                            <button type="button" className="cbarra__boton" onClick={onReintentar}>
                                Reintentar
                            </button>
                        )
                    }
                />
            </div>
        );
    }

    if (cargando) {
        return (
            <div className="cagenda">
                <Esqueleto columnas={Math.max(doctores.length, 3)} />
            </div>
        );
    }

    if (!haySeleccion) {
        return (
            <div className="cagenda">
                <Vacio
                    titulo="Elige una especialidad"
                    texto="Selecciona una especialidad en la lista de la izquierda para ver su agenda del día."
                />
            </div>
        );
    }

    if (doctores.length === 0 || franjas.length === 0) {
        return (
            <div className="cagenda">
                <Vacio
                    titulo="Sin agenda para este día"
                    texto={`Ningún médico de esta especialidad tiene agenda el ${moment(fecha).format("D [de] MMMM")}.`}
                    accion={
                        onIrDiaSiguiente && (
                            <button type="button" className="cbarra__boton" onClick={onIrDiaSiguiente}>
                                Ver el día siguiente
                            </button>
                        )
                    }
                />
            </div>
        );
    }

    const plantilla = `76px repeat(${doctores.length}, minmax(160px, 1fr))`;

    return (
        <div className="cagenda">
            <div className="cagenda__scroll" ref={scrollRef}>
                <div className="cagenda__grid" ref={gridRef} style={{ gridTemplateColumns: plantilla }}>
                    {/* cabecera: fondo uniforme para TODAS las columnas */}
                    <div className="cagenda__cab-hora">
                        <span>Hora</span>
                    </div>
                    {doctores.map((doctor) => {
                        const ocupacion = ocupacionDeDoctor(doctor, franjas, citas, fecha);
                        const nombre = tituloClinico(doctor.nombre);
                        // El color configurado en la ficha del médico identifica
                        // su columna. Va en el avatar y en una barra de 3px, no
                        // como fondo: hay médicos en amarillo o verde lima.
                        const color = colorDoctor(doctor);
                        return (
                            <div className="cagenda__cab-celda" key={doctor.iddoctor}>
                                {color && (
                                    <span
                                        className="cagenda__cab-color"
                                        style={{ background: color }}
                                        aria-hidden="true"
                                    />
                                )}
                                <span
                                    className="cagenda__avatar"
                                    style={color ? { background: color, color: contrasteSobre(color) } : undefined}
                                >
                                    {iniciales(nombre)}
                                </span>
                                <span className="cagenda__medico">
                                    <span className="cagenda__medico-nombre" title={nombre}>{nombre}</span>
                                    <span className="cagenda__medico-detalle">
                                        {ocupacion
                                            ? `${rangoHorario(doctor)} · ${ocupacion.ocupadas}/${ocupacion.total}`
                                            : "No atiende este día"}
                                    </span>
                                </span>
                            </div>
                        );
                    })}

                    {/* filas */}
                    {franjas.map((hora) => (
                        <FilaAgenda
                            key={hora}
                            hora={hora}
                            doctores={doctores}
                            citas={citas}
                            fecha={fecha}
                            intervalo={intervalo}
                            estadosVisibles={estadosVisibles}
                            filtroCobertura={filtroCobertura}
                            soloLectura={soloLectura}
                            onCupo={onCupo}
                            onCitaPulsada={onCitaPulsada}
                            onMenuContextual={onMenuContextual}
                        />
                    ))}

                    {/* Dentro de la cuadrícula: es la que define las horas. Fuera,
                        el `top` se medía contra la caja con scroll. */}
                    {topAhora != null && (
                        <div className="cagenda__ahora" style={{ top: `${topAhora}px` }}>
                            <span className="cagenda__ahora-pastilla">{moment().format("HH:mm")}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="cagenda__leyenda">
                {LEYENDA_ESTADOS.map((estado) => (
                    <span className="cagenda__leyenda-item" key={estado.id}>
                        <span
                            className={`cagenda__leyenda-muestra cagenda__cita--${estado.clave}`}
                            aria-hidden="true"
                        />
                        {estado.etiqueta}
                    </span>
                ))}
                {!soloLectura && (
                    <span className="cagenda__leyenda-ayuda">Clic en un cupo libre para agendar</span>
                )}
            </div>
        </div>
    );
}

function FilaAgenda({ hora, doctores, citas, fecha, intervalo, estadosVisibles, filtroCobertura, soloLectura, onCupo, onCitaPulsada, onMenuContextual }) {
    return (
        <>
            <div className="cagenda__hora">
                <span>{hora}</span>
            </div>
            {doctores.map((doctor) => {
                const laboral = esDiaLaboral(doctor, fecha);
                const enHorario = laboral && estaDentroDeHorario(doctor, hora);
                const cita = enHorario ? buscarCita(citas, doctor, hora, fecha) : null;

                // El menú contextual sale en CUALQUIER celda, con o sin cita.
                const menu = (e) => onMenuContextual?.(e, doctor, hora);

                if (!enHorario) {
                    return (
                        <div
                            className="cagenda__celda cagenda__celda--fuera"
                            key={doctor.iddoctor}
                            onContextMenu={menu}
                            title={laboral ? "Fuera de su horario" : "No atiende este día"}
                        />
                    );
                }

                if (cita) {
                    const estado = estadoDeCita(cita);
                    const paciente = tituloClinico(cita.pacientes?.nombres_completos ?? "");
                    const idEstado = Number(cita.estado_cita?.idestado_cita);
                    /* Los dos filtros se combinan: "cubierto" + "sin
                       confirmar" atenúa todo lo que no sea ambas cosas. El de
                       estado ya existía; el de cobertura es el nuevo. Se
                       ATENÚA, no se oculta -- un cupo ocupado que
                       desapareciera se vería libre. */
                    const fueraPorEstado =
                        estadosVisibles !== null && !estadosVisibles.includes(idEstado);
                    const fueraPorCobertura = !citaCoincideCobertura(cita, filtroCobertura);
                    const atenuada = fueraPorEstado || fueraPorCobertura;
                    const clasesCita = `cagenda__cita cagenda__cita--${estado.clave}${
                        atenuada ? " cagenda__cita--atenuada" : ""
                    }${soloLectura ? " cagenda__cita--estatica" : ""}`;
                    // En sólo lectura la tarjeta sigue siendo pulsable si hay
                    // algo que hacer con ella; sólo queda inerte cuando de
                    // verdad no hay ninguna acción.
                    const alPulsar = soloLectura
                        ? (onCitaPulsada ? (e) => onCitaPulsada(cita, e) : null)
                        : () => onCupo?.(doctor, hora, intervalo);

                    /* CITAS-COBERTURA: a qué pertenece el paciente. Antes
                       sólo se veía el nombre, así que no se sabía si era
                       particular, de seguro o de empresa hasta abrir la cita
                       -- y eso decide si se cobra en el momento o no.

                       El dato ya existe en `pacientes`; sólo faltaba pedirlo
                       en el `select` de la agenda. Si no viene, no se pinta
                       nada: un hueco es más honesto que un "Particular"
                       supuesto. */
                    const cobertura = coberturaDeCita(cita);

                    const contenido = (
                        <>
                            <span className="cagenda__cita-nombre">{nombreAbreviado(paciente)}</span>
                            <span className="cagenda__cita-pie">
                                <span className="cagenda__cita-tipo">{estado.etiqueta}</span>
                                {cobertura && (
                                    <span
                                        className={`cagenda__cobertura cagenda__cobertura--${cobertura.tono}`}
                                    >
                                        {cobertura.etiqueta}
                                    </span>
                                )}
                            </span>
                        </>
                    );
                    // El detalle completo (tipo, aseguradora y plan) va en el
                    // `title`: en la tarjeta no cabe, pero a un hover sí.
                    const detalleCobertura = cobertura ? ` · ${textoCobertura(cobertura)}` : "";
                    const titulo = soloLectura && onCitaPulsada
                        ? `${paciente} · ${estado.etiqueta}${detalleCobertura} — ver acciones`
                        : `${paciente} · ${estado.etiqueta}${detalleCobertura}`;

                    return (
                        <div className="cagenda__celda" key={doctor.iddoctor} onContextMenu={menu}>
                            {alPulsar ? (
                                <button
                                    type="button"
                                    className={`${clasesCita}${soloLectura ? " cagenda__cita--abrible" : ""}`}
                                    onClick={alPulsar}
                                    title={titulo}
                                >
                                    {contenido}
                                </button>
                            ) : (
                                <div className={clasesCita} title={titulo}>{contenido}</div>
                            )}
                        </div>
                    );
                }

                if (soloLectura) {
                    // Libre y sin nada que ofrecer: la celda queda vacía, no
                    // con un botón que no lleva a ningún sitio.
                    return <div className="cagenda__celda" key={doctor.iddoctor} />;
                }

                return (
                    <div className="cagenda__celda" key={doctor.iddoctor} onContextMenu={menu}>
                        <button
                            type="button"
                            className="cagenda__libre"
                            onClick={() => onCupo?.(doctor, hora, intervalo)}
                            aria-label={`Agendar a las ${hora}`}
                        >
                            <IconoMas />
                            Agendar
                        </button>
                    </div>
                );
            })}
        </>
    );
}
